import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  LuArrowRight,
  LuCalendar,
  LuCheck,
  LuChevronLeft,
  LuChevronRight,
  LuClock,
  LuDownload,
  LuEye,
  LuFlame,
  LuHistory,
  LuLayoutGrid,
  LuLightbulb,
  LuList,
  LuMessageSquare,
  LuMic,
  LuPlay,
  LuSparkles,
  LuStar,
  LuTrendingUp,
  LuTrophy,
  LuVideo,
  LuX,
} from "react-icons/lu";
import { Btn, Badge, Field, Spinner, Skel, Empty, Note, Dialog } from "../components/Ui";
import {
  setQuestionAnswer,
  setDocId,
  setAnswers,
  setScenarioBased,
  setInterviewLevel,
  setIndustryInterview,
} from "../../store/actions/actions";
import "./AspireQuest.css";

/* Endpoints — identical to legacy aspireQuest.jsx / feedbackInterviewList.jsx */
const V4_API = "https://interview-prep-function-v4-980069659423.us-east1.run.app";
const V6_API = "https://interview-prep-function-v6-980069659423.us-east1.run.app";
const IP_V2_API = "https://us-east1-foursssolutions.cloudfunctions.net/interview_prep_function_v2";
const FEEDBACK_API = "https://aspire-quest-interview-tool-v2-980069659423.us-east1.run.app";
const FEEDBACK_DOC_API = "https://aspire-quest-interview-tool-doc-v2-980069659423.us-east1.run.app";

const STEPS = ["Interview type", "Configure session", "Check & start"];

/* pagination range helper — carried over from legacy */
function getPaginationRange(totalPages, currentPage, siblingCount = 1) {
  const totalNumbers = siblingCount * 2 + 5;
  const range = [];
  if (totalPages <= totalNumbers) {
    for (let i = 1; i <= totalPages; i += 1) range.push(i);
  } else {
    const left = Math.max(currentPage - siblingCount, 2);
    const right = Math.min(currentPage + siblingCount, totalPages - 1);
    range.push(1);
    if (left > 2) range.push("...");
    for (let i = left; i <= right; i += 1) range.push(i);
    if (right < totalPages - 1) range.push("...");
    range.push(totalPages);
  }
  return range;
}

/* star strip for text results — colors identical to legacy */
function StarRating({ rating }) {
  return (
    <span className="row" style={{ gap: 2 }}>
      {[...Array(10)].map((_, i) => {
        let color = "#d1d5db";
        if (rating >= i + 1) {
          if (rating <= 2) color = "#ef4444";
          else if (rating <= 7) color = "#facc15";
          else color = "#22c55e";
        }
        return <LuStar key={i} size={15} fill={color} style={{ color }} />;
      })}
    </span>
  );
}

/* ---------- video interview feedback (same fetches as legacy Feedback component) ---------- */
function VideoFeedback({ docId }) {
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let alive = true;
    const fetchFeedback = async () => {
      try {
        const response = await fetch(FEEDBACK_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "Get_Feedback", doc_id: docId }),
        });
        if (response.ok) {
          const data = await response.json();
          if (alive) setFeedback(data);
        } else {
          console.error("Error fetching feedback", await response.text());
          if (alive) setError("We could not load the feedback for this interview.");
        }
      } catch (err) {
        console.error("Network error:", err);
        if (alive) setError("We could not load the feedback for this interview.");
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchFeedback();
    return () => {
      alive = false;
    };
  }, [docId]);

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const response = await fetch(FEEDBACK_DOC_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "Get_Feedback_doc", doc_id: docId }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.pdf_data) {
          const byteCharacters = atob(data.pdf_data);
          const byteNumbers = Array.from(byteCharacters).map((c) => c.charCodeAt(0));
          const blob = new Blob([new Uint8Array(byteNumbers)], { type: "application/pdf" });
          const now = new Date();
          const stamp = `${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}-${now.getFullYear()}_${String(now.getHours()).padStart(2, "0")}-${String(now.getMinutes()).padStart(2, "0")}-${String(now.getSeconds()).padStart(2, "0")}`;
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = `interview_report_${stamp}.pdf`;
          link.click();
          URL.revokeObjectURL(link.href);
        } else {
          toast.error("The report is not available yet. Please try again later.");
        }
      } else {
        console.error("Error fetching feedback", await response.text());
        toast.error("Could not download the analysis report.");
      }
    } catch (err) {
      console.error("Network error:", err);
      toast.error("Could not download the analysis report.");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="stack-3">
        <Skel h={20} w="45%" />
        <Skel h={10} />
        <Skel h={10} w="80%" />
        <Skel h={90} />
      </div>
    );
  }
  if (error) return <Note tone="danger">{error}</Note>;
  if (!feedback || Object.keys(feedback).length === 0) {
    return <Empty icon={<LuVideo size={22} />} title="No feedback available" sub="This interview has no AI feedback yet." />;
  }

  const overall = Number(feedback.overall_score || 85);
  return (
    <div className="stack-4">
      <div>
        <div className="row-between mb-1">
          <span className="t-sm t-ink" style={{ fontWeight: 600 }}>
            Overall performance
          </span>
          <span className="t-h3 t-accent">{overall}%</span>
        </div>
        <div className="bar">
          <span style={{ width: `${Math.max(0, Math.min(100, overall))}%` }} />
        </div>
      </div>

      {(feedback.strengths || []).length > 0 ? (
        <div>
          <div className="aq-minihead">Your strengths</div>
          <ul className="stack-2">
            {(feedback.strengths || []).map((item, i) => (
              <li key={i} className="row t-sm" style={{ alignItems: "flex-start" }}>
                <LuCheck size={15} style={{ color: "var(--accent-strong)", marginTop: 3, flex: "0 0 auto" }} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {(feedback.areas_for_improvement || []).length > 0 ? (
        <div>
          <div className="aq-minihead">Areas for improvement</div>
          <ul className="stack-2">
            {(feedback.areas_for_improvement || []).map((item, i) => (
              <li key={i} className="row t-sm" style={{ alignItems: "flex-start" }}>
                <LuTrendingUp size={15} style={{ color: "var(--amber)", marginTop: 3, flex: "0 0 auto" }} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {Object.keys(feedback.ratings || {}).length > 0 ? (
        <div>
          <div className="aq-minihead">Performance metrics</div>
          <div className="stack-3">
            {Object.entries(feedback.ratings || {}).map(([key, value]) => (
              <div key={key}>
                <div className="row-between mb-1">
                  <span className="t-sm" style={{ textTransform: "capitalize" }}>
                    {key.replace(/_/g, " ")}
                  </span>
                  <span className="t-sm t-ink" style={{ fontWeight: 600 }}>
                    {value}/10
                  </span>
                </div>
                <div className="bar">
                  <span style={{ width: `${(Number(value) / 10) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {(feedback.analysis_highlights || []).length > 0 ? (
        <div>
          <div className="aq-minihead">Key insights</div>
          <ol className="stack-2" style={{ listStyle: "decimal", paddingLeft: 18 }}>
            {(feedback.analysis_highlights || []).map((item, i) => (
              <li key={i} className="t-sm">
                {item}
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      <div>
        <Btn variant="ghost" className="btn-pill" onClick={downloadReport} loading={downloading}>
          <LuDownload size={16} /> Download analysis report
        </Btn>
      </div>
    </div>
  );
}

/* ---------- system check (behaviour carried over from legacy SystemCheckPopup at /aspire-quest) ---------- */
function SystemCheck({ open, genStatus, onCancel, onContinue }) {
  const [progress, setProgress] = useState(0);
  const [micOn, setMicOn] = useState(false);
  const [micLevel, setMicLevel] = useState(0);
  const [transcript, setTranscript] = useState("");
  const micStreamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const dataRef = useRef(null);
  const recogRef = useRef(null);
  const runningRef = useRef(false);

  useEffect(() => {
    if (!open) return undefined;
    setProgress(0);
    /* legacy TriggerAudio: prompt the mic permission as soon as the check opens */
    (async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (err) {
        console.error(err);
      }
    })();
    const interval = setInterval(() => {
      setProgress((prev) => (prev < 100 ? prev + 1 : 100));
    }, 139);
    return () => clearInterval(interval);
  }, [open]);

  const updateMicLevel = () => {
    if (!runningRef.current || !analyserRef.current || !dataRef.current) return;
    analyserRef.current.getByteFrequencyData(dataRef.current);
    const volume = dataRef.current.reduce((a, b) => a + b, 0) / dataRef.current.length;
    setMicLevel(volume);
    requestAnimationFrame(updateMicLevel);
  };

  const startMicTest = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioCtx();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      audioCtxRef.current = audioContext;
      analyserRef.current = analyser;
      dataRef.current = new Uint8Array(analyser.frequencyBinCount);
      runningRef.current = true;
      setMicOn(true);
      requestAnimationFrame(updateMicLevel);

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = "en-US";
        recognition.onresult = (event) => {
          let current = "";
          for (let i = event.resultIndex; i < event.results.length; i += 1) {
            current += event.results[i][0].transcript;
          }
          setTranscript(current);
        };
        recognition.onerror = (e) => console.error("Recognition error:", e);
        recognition.start();
        recogRef.current = recognition;
      } else {
        console.warn("SpeechRecognition not supported in this browser.");
      }
    } catch (error) {
      toast.error("Microphone access denied.");
      console.error(error);
    }
  };

  const stopMicTest = () => {
    runningRef.current = false;
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
    }
    if (audioCtxRef.current) {
      try {
        audioCtxRef.current.close();
      } catch (e) {
        /* noop */
      }
      audioCtxRef.current = null;
      analyserRef.current = null;
    }
    if (recogRef.current) {
      try {
        recogRef.current.stop();
      } catch (e) {
        /* noop */
      }
      recogRef.current = null;
    }
    setMicOn(false);
    setMicLevel(0);
    setTranscript("");
  };

  useEffect(() => () => stopMicTest(), []);

  if (!open) return null;
  return (
    <div className="aq-scrim">
      <div className="aq-modal aq-narrow" role="dialog" aria-modal="true">
        <span className="ld-eyebrow">Before you start</span>
        <div className="t-h3 mt-1 mb-1">System check</div>
        <p className="t-sm mb-3">
          We’re checking your microphone to ensure everything is set up correctly for your interview.
        </p>
        <div className="bar mb-3">
          <span style={{ width: `${progress}%` }} />
        </div>

        {progress === 100 ? (
          <div className="stack-3">
            <div className="row-between">
              <span className="row t-sm t-ink" style={{ fontWeight: 600 }}>
                <LuMic size={16} style={{ color: "var(--accent-strong)" }} /> Microphone
              </span>
              {!micOn ? (
                <Btn variant="ghost" className="btn-pill" size="sm" onClick={startMicTest}>
                  Test microphone
                </Btn>
              ) : (
                <Btn variant="danger-soft" className="btn-pill" size="sm" onClick={stopMicTest}>
                  Stop test
                </Btn>
              )}
            </div>
            {micOn ? (
              <div className="stack-2">
                <div className="aq-mic-meter">
                  <span style={{ width: `${Math.min(100, micLevel)}%` }} />
                </div>
                <p className="t-xs">{transcript || "Speak into your microphone..."}</p>
              </div>
            ) : null}

            {genStatus === "generating" ? (
              <Note tone="info">
                <Spinner /> <span>Your interview questions are being generated…</span>
              </Note>
            ) : genStatus === "ready" ? (
              <Note tone="accent">
                <LuCheck size={16} /> <span>Your interview questions are ready.</span>
              </Note>
            ) : genStatus === "error" ? (
              <Note tone="danger">Question generation failed. You can cancel and try again.</Note>
            ) : null}

            <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
              <Btn
                variant="ghost"
                className="btn-pill"
                onClick={() => {
                  stopMicTest();
                  onCancel();
                }}
              >
                Cancel
              </Btn>
              <Btn
                variant="mint"
                className="btn-pill"
                onClick={() => {
                  stopMicTest();
                  onContinue();
                }}
              >
                Continue
              </Btn>
            </div>
          </div>
        ) : (
          <p className="t-xs">Running checks…</p>
        )}
      </div>
    </div>
  );
}

/* ============================================================ */
export default function AspireQuest() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userEmail = useSelector((state) => state.auth.auth.email);
  const { apiToken, apiTokenReady, scenarioBased, interviewLevel, industryInterview } = useSelector(
    (state) => state.profile
  );

  const [activeTab, setActiveTab] = useState("start");
  const [interviews, setInterviews] = useState([]);
  const [videoInterviewData, setVideoInterviewData] = useState([]);
  const [tips, setTips] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState("");
  const [viewType, setViewType] = useState("card");
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(6);

  /* quest steps */
  const [step, setStep] = useState(0);
  const [interviewType, setInterviewType] = useState("");
  const [level, setLevel] = useState("beginner");
  const [jobDescription, setJobDescription] = useState("");

  const [checkOpen, setCheckOpen] = useState(false);
  const [genStatus, setGenStatus] = useState("");
  const [busy, setBusy] = useState("");
  const [tipsOpen, setTipsOpen] = useState(false);

  const [resultOpen, setResultOpen] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [resultError, setResultError] = useState("");
  const [videoDocId, setVideoDocId] = useState("");

  /* legacy parity: aspireQuest.jsx ran dispatch(setQuestionAnswer(questions)) in an
     effect on [questions] whose initial value was [], so landing on this page
     cleared any stale exam questions from redux. Restore that mount-time guard. */
  useEffect(() => {
    dispatch(setQuestionAnswer([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setRecordsPerPage(viewType === "card" ? 6 : 4);
    setCurrentPage(1);
  }, [viewType]);

  /* keep redux in sync with the configuration step (legacy InterviewConfigModal behaviour) */
  useEffect(() => {
    if (step >= 1) {
      dispatch(setInterviewLevel(level));
      dispatch(setIndustryInterview(jobDescription));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, level, jobDescription]);

  /* ---------- fetches (payloads identical to legacy) ---------- */
  const fetchInterviews = async () => {
    const payload = { email: userEmail, task: "fetch_all_interviews" };
    try {
      const response = await fetch(V4_API, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiToken}` },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        const data = await response.json();
        setInterviews(data.response || []);
        if (data.video_interview) setVideoInterviewData(data.video_interview);
        setHistoryError("");
      } else {
        console.error("Error fetching interviews");
        setHistoryError("We could not load your interview history.");
      }
    } catch (error) {
      console.error("Error:", error);
      setHistoryError("We could not load your interview history.");
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchTips = async () => {
    const payload = { email: userEmail, task: "fetch_tips" };
    try {
      const response = await fetch(IP_V2_API, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiToken}` },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        const data = await response.json();
        setTips(data.response[0] || []);
      } else {
        console.error("Error fetching interviews");
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  useEffect(() => {
    if (apiToken !== "" && userEmail !== "") {
      const t = setTimeout(() => {
        fetchInterviews();
        fetchTips();
      }, 1000);
      dispatch(setIndustryInterview(""));
      dispatch(setInterviewLevel(""));
      dispatch(setScenarioBased(false));
      return () => clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiTokenReady]);

  const retryHistory = () => {
    setHistoryLoading(true);
    setHistoryError("");
    fetchInterviews();
  };

  /* ---------- resume an incomplete interview (identical to legacy startExam) ---------- */
  const startExam = async (interview) => {
    setBusy("Resuming your interview…");
    const payLoad = { task: "fetch_specific_interview", email: userEmail, doc_id: interview.id };
    try {
      const response = await fetch(V4_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payLoad),
      });
      if (response.ok) {
        const data = await response.json();
        localStorage.removeItem("answers");
        localStorage.removeItem("questionAnswer");
        dispatch(setQuestionAnswer(data.response.questions));
        dispatch(setDocId(data.response.doc_id));
        dispatch(setAnswers(data.response.answers));
        dispatch(setInterviewLevel(data.response.level));
        localStorage.setItem("examSource", "aspire-quest"); // label/back-nav context (#3)
        setTimeout(() => {
          navigate("/interview-exam");
        }, 1000);
      } else {
        console.error("Error creating exam:", await response.text());
        toast.error("Failed to create exam. Please try again.");
        setBusy("");
      }
    } catch (error) {
      console.error("Error creating exam:", error);
      toast.error("Error creating exam: " + error);
      setBusy("");
    }
  };

  /* ---------- open text results (identical to legacy fetchTextSpecific) ---------- */
  function openResults(interview) {
    setResultData(null);
    setResultError("");
    setResultOpen(true);
    fetchTextSpecific(interview);
  }

  const fetchTextSpecific = async (interview) => {
    const payLoad = { task: "fetch_specific_interview", email: userEmail, doc_id: interview.id };
    try {
      const response = await fetch(V4_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payLoad),
      });
      if (response.ok) {
        const data = await response.json();
        setResultData(data.response);
      } else {
        console.error("Error creating exam:", await response.text());
        setResultError("We could not load these interview results.");
      }
    } catch (error) {
      console.error("Error creating exam:", error);
      setResultError("We could not load these interview results.");
    }
  };

  function interviewdetails(interviewData) {
    setVideoDocId(interviewData.id);
  }

  /* ---------- create exam via streaming v6 endpoint (identical to legacy CreateExam) ---------- */
  const CreateExam = async () => {
    setGenStatus("generating");
    try {
      const response = await fetch(V6_API, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiToken}` },
        body: JSON.stringify({
          task: "create_exam",
          email: userEmail,
          level: interviewLevel,
          scenario_based: scenarioBased,
          job_desc: industryInterview,
        }),
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let fullContent = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (let line of lines) {
          line = line.trim();
          if (line.startsWith("data:")) {
            try {
              const jsonLine = JSON.parse(line.replace("data:", "").trim());
              if (jsonLine?.doc_id) {
                dispatch(setDocId(jsonLine.doc_id));
              }
              if (jsonLine?.content) {
                fullContent += jsonLine.content;
              }
            } catch (err) {
              /* silently skip malformed lines (legacy behaviour) */
            }
          }
        }
      }

      const parsed = JSON.parse(fullContent);
      const entries = Object.entries(parsed).filter(([key]) => !isNaN(key));
      const newQuestions = entries.map(([id, text]) => ({ id: parseInt(id, 10), question: text }));

      localStorage.removeItem("answers");
      localStorage.removeItem("questionAnswer");
      dispatch(setAnswers([]));

      /* legacy convertToFlatArrayAndSet */
      const flatArray = newQuestions.map((item) => {
        const questionData = item.question;
        const questionNumber = Object.keys(questionData).find(
          (key) => key !== "initialize_code" && key !== "language"
        );
        return {
          [questionNumber]: questionData[questionNumber],
          initialize_code: questionData.initialize_code,
          language: questionData.language,
        };
      });
      dispatch(setQuestionAnswer(flatArray));
      setGenStatus("ready");
    } catch (error) {
      console.error("Error creating exam:", error);
      toast.error("Error creating exam: " + error);
      setGenStatus("error");
    }
  };

  const startQuest = () => {
    if (interviewType === "video-based") {
      navigate("/videoInterview");
      return;
    }
    /* text-based: kick generation and run the system check (legacy handleSubmit + SystemCheckPopup) */
    CreateExam();
    setCheckOpen(true);
  };

  /* ---------- history normalisation (identical to legacy) ---------- */
  const normalizedInterviews = interviews.map((item) => ({
    id: item.doc_id,
    type: "text",
    completed: item.completed,
    datetime: item.datetime || item.data?.datetime || null,
    level: item.level || item.data?.level || "Unknown",
    status: item.completed ? "completed" : "in_progress",
  }));
  const normalizedVideoInterviews = videoInterviewData.map((item) => ({
    id: item.id,
    type: "video",
    completed: item.status === "completed",
    datetime: item.date || null,
    level: item.level || "Unknown",
    status: item.status,
  }));
  const combinedInterviews = [...normalizedInterviews, ...normalizedVideoInterviews].sort(
    (a, b) => new Date(b.datetime || 0) - new Date(a.datetime || 0)
  );

  const totalRecords = combinedInterviews.length;
  const totalPages = Math.ceil(totalRecords / recordsPerPage);
  const paginatedInterviews = combinedInterviews.slice(
    (currentPage - 1) * recordsPerPage,
    currentPage * recordsPerPage
  );
  const tableData = paginatedInterviews.map((item) => ({
    date: item.datetime ? new Date(item.datetime).toLocaleDateString("en-US") : "N/A",
    type: item.type === "video" ? "Video Interview" : "Text Interview",
    level: item.level || "Unknown",
    status: item.completed ? "Completed" : "In Progress",
    action: !item.completed,
    id: item.id,
    completed: item.completed,
    rawType: item.type,
  }));

  const hasHistory = interviews.length > 1 || videoInterviewData.length > 0;

  /* hero stats — real history data (Lovable's XP / rank mock counters are skipped) */
  const completedSessions = combinedInterviews.filter((i) => i.completed).length;
  const inProgressSessions = totalRecords - completedSessions;
  const overallPct = totalRecords > 0 ? Math.round((completedSessions / totalRecords) * 100) : 0;

  /* ============================ render ============================ */
  return (
    <div className="stack-4">
      {/* ---------- quest hero ---------- */}
      <section className="aq-hero">
        <div className="aq-hero-mesh" aria-hidden="true" />
        <div className="aq-hero-in">
          <span className="ld-kicker">
            <LuTrophy size={13} /> Aspire Quest
          </span>
          <h1 className="aq-hero-title">Rehearse the interview before it happens</h1>
          <p className="aq-hero-sub">
            Configure a guided session, run a quick system check, and practice text or video interviews with AI
            feedback on every answer.
          </p>

          <div className="aq-hero-stats">
            <div className="aq-stat">
              <p className="aq-stat-n">{historyLoading ? "—" : `${completedSessions}/${totalRecords}`}</p>
              <p className="aq-stat-l">Sessions completed</p>
            </div>
            <div className="aq-stat">
              <p className="aq-stat-n">{historyLoading ? "—" : inProgressSessions}</p>
              <p className="aq-stat-l">In progress</p>
            </div>
            <div className="aq-stat">
              <p className="aq-stat-n">{historyLoading ? "—" : videoInterviewData.length}</p>
              <p className="aq-stat-l">Video sessions</p>
            </div>
          </div>

          <div className="aq-hero-progress">
            <div className="row-between">
              <span className="row aq-progress-label" style={{ gap: 6 }}>
                <LuFlame size={15} style={{ color: "var(--accent)" }} /> Overall progress
              </span>
              <span className="aq-progress-label">{historyLoading ? 0 : overallPct}%</span>
            </div>
            <div className="ld-bar mt-1">
              <span style={{ width: `${historyLoading ? 0 : overallPct}%` }} />
            </div>
          </div>
        </div>
      </section>

      {/* ---------- tabs ---------- */}
      <div className="aq-tabsrow">
        <div className="tabs">
          <button
            type="button"
            className={`tab${activeTab === "start" ? " on" : ""}`}
            onClick={() => setActiveTab("start")}
          >
            <span className="row" style={{ gap: 7 }}>
              <LuPlay size={15} /> Start interview
            </span>
          </button>
          <button
            type="button"
            className={`tab${activeTab === "history" ? " on" : ""}`}
            onClick={() => setActiveTab("history")}
          >
            <span className="row" style={{ gap: 7 }}>
              <LuHistory size={15} /> Interview history
            </span>
          </button>
        </div>
        <Btn variant="ghost" className="btn-pill" size="sm" onClick={() => setTipsOpen(true)}>
          <LuLightbulb size={14} /> Exam tips
        </Btn>
      </div>

      {activeTab === "start" ? (
        <section className="ld-glass aq-panel">
          {/* step progress */}
          <div className="mb-3">
            <div className="row-between mb-1">
              <span className="ld-eyebrow">
                Step {step + 1} of {STEPS.length} — {STEPS[step]}
              </span>
              <span className="t-xs">{Math.round(((step + 1) / STEPS.length) * 100)}%</span>
            </div>
            <div className="bar">
              <span style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
            </div>
          </div>

          {step === 0 ? (
            <div className="stack-4">
              <div>
                <h2 className="t-h2">Choose a track</h2>
                <p className="t-sm mt-1">Pick the type of interview you want to practice.</p>
              </div>
              <div className="grid-2">
                <button
                  type="button"
                  className={`aq-track${interviewType === "text-based" ? " on" : ""}`}
                  onClick={() => {
                    setInterviewType("text-based");
                    setStep(1);
                  }}
                >
                  <span className="aq-track-top">
                    <span
                      className="aq-track-tag"
                      style={{ backgroundColor: "var(--accent-soft)", color: "var(--accent-on-soft)" }}
                    >
                      Chat format
                    </span>
                    <LuSparkles size={18} style={{ color: "var(--accent)" }} />
                  </span>
                  <span className="aq-track-name">Text-based interview</span>
                  <span className="t-sm">Answer questions in text format, at your own pace.</span>
                  <span className="aq-track-cta">
                    Configure session <LuArrowRight size={14} />
                  </span>
                </button>
                <button
                  type="button"
                  className={`aq-track${interviewType === "video-based" ? " on" : ""}`}
                  onClick={() => {
                    setInterviewType("video-based");
                    setStep(1);
                  }}
                >
                  <span className="aq-track-top">
                    <span
                      className="aq-track-tag"
                      style={{ backgroundColor: "var(--info-soft)", color: "var(--info-on-soft)" }}
                    >
                      On camera
                    </span>
                    <LuVideo size={18} style={{ color: "var(--accent)" }} />
                  </span>
                  <span className="aq-track-name">Video-based interview</span>
                  <span className="t-sm">Answer questions with recorded video, like the real thing.</span>
                  <span className="aq-track-cta">
                    Configure session <LuArrowRight size={14} />
                  </span>
                </button>
              </div>

              <div>
                <div className="ld-eyebrow mb-2">How it works</div>
                <div className="grid-3">
                  {[
                    ["Select interview type", "Choose between text or video-based interview formats"],
                    ["Configure your session", "Customize difficulty and provide job description details"],
                    ["Practice and improve", "Get feedback and improve your interview skills"],
                  ].map(([title, sub], i) => (
                    <div key={i} className="row" style={{ alignItems: "flex-start" }}>
                      <span className="aq-stepnum">{i + 1}</span>
                      <div>
                        <div className="t-sm t-ink" style={{ fontWeight: 600 }}>
                          {title}
                        </div>
                        <div className="t-xs mt-1">{sub}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : step === 1 ? (
            <div className="stack-4" style={{ maxWidth: 560 }}>
              <div>
                <h2 className="t-h2">Configure your interview</h2>
                <p className="t-sm mt-1">Customize your interview experience.</p>
              </div>
              <Field label="Interview level">
                <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                  {["beginner", "intermediate", "advanced"].map((lvl) => (
                    <button
                      key={lvl}
                      type="button"
                      className={`chip${level === lvl ? " on" : ""}`}
                      onClick={() => setLevel(lvl)}
                    >
                      {level === lvl ? <LuCheck size={14} /> : null}
                      {lvl.charAt(0).toUpperCase() + lvl.slice(1)}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Interview format">
                <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className={`chip${scenarioBased ? " on" : ""}`}
                    onClick={() => dispatch(setScenarioBased(true))}
                  >
                    {scenarioBased ? <LuCheck size={14} /> : null} Scenario-based
                  </button>
                  <button
                    type="button"
                    className={`chip${!scenarioBased ? " on" : ""}`}
                    onClick={() => dispatch(setScenarioBased(false))}
                  >
                    {!scenarioBased ? <LuCheck size={14} /> : null} Non-scenario-based
                  </button>
                </div>
              </Field>
              <Field
                label="Job description (optional)"
                hint="Providing a job description will help generate more relevant questions for your target role. If not provided, questions will be generated based on your resume and selected skill level."
              >
                <textarea
                  className="input"
                  placeholder="Paste job description for more targeted questions..."
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                />
              </Field>
              <div className="row" style={{ gap: 10 }}>
                <Btn variant="ghost" className="btn-pill" onClick={() => setStep(0)}>
                  <LuChevronLeft size={15} /> Back
                </Btn>
                <Btn variant="mint" className="btn-pill" onClick={() => setStep(2)}>
                  Continue <LuChevronRight size={15} />
                </Btn>
              </div>
            </div>
          ) : (
            <div className="stack-4" style={{ maxWidth: 560 }}>
              <div>
                <h2 className="t-h2">Ready to start</h2>
                <p className="t-sm mt-1">
                  We will run a quick system check before your {interviewType === "video-based" ? "video" : "text"}{" "}
                  interview begins.
                </p>
              </div>
              <div className="stack-2">
                <div className="aq-row">
                  <span>Interview type</span>
                  <b>{interviewType === "video-based" ? "Video-based" : "Text-based"}</b>
                </div>
                <div className="aq-row">
                  <span>Level</span>
                  <b style={{ textTransform: "capitalize" }}>{level}</b>
                </div>
                <div className="aq-row">
                  <span>Format</span>
                  <b>{scenarioBased ? "Scenario-based" : "Non-scenario-based"}</b>
                </div>
                <div className="aq-row">
                  <span>Job description</span>
                  <b>{jobDescription.trim() ? "Provided" : "Not provided"}</b>
                </div>
              </div>
              <div className="row" style={{ gap: 10 }}>
                <Btn variant="ghost" className="btn-pill" onClick={() => setStep(1)}>
                  <LuChevronLeft size={15} /> Back
                </Btn>
                <Btn variant="mint" className="btn-pill" size="lg" onClick={startQuest}>
                  <LuSparkles size={16} />
                  {interviewType === "video-based" ? "Start video interview" : "Start interview"}
                </Btn>
              </div>
            </div>
          )}
        </section>
      ) : (
        <section className="ld-glass aq-panel">
          <div className="row-between" style={{ flexWrap: "wrap", gap: 10 }}>
            <div>
              <h2 className="t-h2 row" style={{ gap: 8 }}>
                <LuHistory size={18} /> Interview history
              </h2>
              <p className="t-xs mt-1">View your past and incomplete interviews.</p>
            </div>
            <div className="row" style={{ gap: 6 }}>
              <Btn
                variant={viewType === "card" ? "soft" : "ghost"}
                className="btn-pill"
                size="sm"
                onClick={() => setViewType("card")}
              >
                <LuLayoutGrid size={14} /> Card view
              </Btn>
              <Btn
                variant={viewType === "table" ? "soft" : "ghost"}
                className="btn-pill"
                size="sm"
                onClick={() => setViewType("table")}
              >
                <LuList size={14} /> Table view
              </Btn>
            </div>
          </div>

          {historyError ? (
            <div className="mt-3">
              <Note tone="danger">
                <span className="grow">{historyError}</span>
                <button type="button" onClick={retryHistory} style={{ fontWeight: 600, textDecoration: "underline" }}>
                  Retry
                </button>
              </Note>
            </div>
          ) : historyLoading ? (
            <div className="stack-3 mt-3">
              {[0, 1, 2, 3].map((i) => (
                <Skel key={i} h={54} r={18} />
              ))}
            </div>
          ) : !hasHistory ? (
            <div className="ld-empty mt-3">
              <span className="ld-empty-icon">
                <LuHistory size={18} />
              </span>
              <div className="ld-empty-title">No interviews yet</div>
              <p className="ld-empty-sub">Start your first practice interview and it will show up here.</p>
              <div className="mt-2">
                <Btn variant="mint" className="btn-pill" onClick={() => setActiveTab("start")}>
                  <LuPlay size={15} /> Start interview
                </Btn>
              </div>
            </div>
          ) : (
            <div className="mt-3">
              {viewType === "card" ? (
                <div className="grid-3">
                  {paginatedInterviews.map((interview, index) => {
                    const formattedDate = interview.datetime
                      ? new Date(interview.datetime).toLocaleDateString("en-US")
                      : "N/A";
                    return (
                      <div
                        key={interview.id || index}
                        className="card ld-tile-hover aq-hcard"
                        style={{ borderRadius: 18, cursor: "pointer" }}
                        onClick={() => {
                          if (interview.completed && interview.type === "text") {
                            openResults(interview);
                          } else if (interview.type === "video") {
                            interviewdetails(interview);
                          }
                        }}
                      >
                        <div className="row-between" style={{ alignItems: "flex-start" }}>
                          <div className="row">
                            <span className="ld-stat-icon">
                              {interview.type === "video" ? <LuVideo size={17} /> : <LuMessageSquare size={17} />}
                            </span>
                            <div>
                              <div className="aq-hcard-name">
                                {interview.type === "video" ? "Video Interview" : "Text Interview"}
                              </div>
                              <div className="t-xs" style={{ textTransform: "capitalize" }}>
                                {interview.level} level
                              </div>
                            </div>
                          </div>
                          <Badge tone={interview.completed ? "accent" : "amber"}>
                            {interview.completed ? "Completed" : "In Progress"}
                          </Badge>
                        </div>
                        <div className="row-between">
                          <span className="row t-xs" style={{ gap: 6 }}>
                            <LuCalendar size={13} /> {formattedDate}
                          </span>
                          {!interview.completed ? (
                            <Btn
                              size="sm"
                              variant="mint"
                              className="btn-pill"
                              onClick={(e) => {
                                e.stopPropagation();
                                startExam(interview);
                              }}
                            >
                              <LuPlay size={13} /> Resume
                            </Btn>
                          ) : (
                            <Btn size="sm" variant="ghost" className="btn-pill">
                              <LuEye size={13} /> View details
                            </Btn>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="aq-table-wrap">
                  <table className="aq-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Level</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.map((row, idx) => (
                        <tr key={idx}>
                          <td>{row.date}</td>
                          <td>
                            <span className="row" style={{ gap: 7 }}>
                              {row.type === "Video Interview" ? <LuVideo size={15} /> : <LuMessageSquare size={15} />}
                              {row.type}
                            </span>
                          </td>
                          <td style={{ textTransform: "capitalize" }}>{row.level}</td>
                          <td>
                            {row.status === "Completed" ? (
                              <Badge tone="accent">
                                <LuCheck size={12} /> Completed
                              </Badge>
                            ) : (
                              <Badge tone="amber">
                                <LuClock size={12} /> In Progress
                              </Badge>
                            )}
                          </td>
                          <td>
                            {row.action ? (
                              <Btn size="sm" variant="mint" className="btn-pill" onClick={() => startExam(row)}>
                                <LuPlay size={13} /> Resume
                              </Btn>
                            ) : (
                              <Btn
                                size="sm"
                                variant="ghost"
                                className="btn-pill"
                                onClick={() => {
                                  if (row.status === "Completed" && row.type === "Text Interview") {
                                    openResults(row);
                                  } else if (row.type === "Video Interview") {
                                    interviewdetails(row);
                                  }
                                }}
                              >
                                <LuEye size={13} /> View details
                              </Btn>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="t-xs mt-2">A list of your recent interviews.</p>
                </div>
              )}

              {/* pagination */}
              <div className="row mt-3" style={{ justifyContent: "center", gap: 4, flexWrap: "wrap" }}>
                <Btn
                  variant="ghost"
                  className="btn-pill"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                >
                  <LuChevronLeft size={14} /> Previous
                </Btn>
                {getPaginationRange(totalPages, currentPage).map((page, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className={`aq-page-num${currentPage === page ? " on" : ""}${page === "..." ? " dots" : ""}`}
                    onClick={() => typeof page === "number" && setCurrentPage(page)}
                  >
                    {page}
                  </button>
                ))}
                <Btn
                  variant="ghost"
                  className="btn-pill"
                  size="sm"
                  disabled={currentPage === totalPages || totalPages === 0}
                  onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
                >
                  Next <LuChevronRight size={14} />
                </Btn>
              </div>
            </div>
          )}
        </section>
      )}

      {/* ---------- system check (mic) → /interview-exam ---------- */}
      <SystemCheck
        open={checkOpen}
        genStatus={genStatus}
        onCancel={() => {
          setCheckOpen(false);
          setGenStatus("");
        }}
        onContinue={() => {
          setCheckOpen(false);
          navigate("/interview-exam");
        }}
      />

      {/* ---------- tips dialog ---------- */}
      <Dialog open={tipsOpen} onClose={() => setTipsOpen(false)}>
        <div className="row-between mb-2">
          <div className="t-h3">Tips for interview exam</div>
          <button type="button" onClick={() => setTipsOpen(false)} aria-label="Close" style={{ color: "var(--ink-3)" }}>
            <LuX size={18} />
          </button>
        </div>
        <div className="stack-2">
          {Object.entries(tips || {}).map(([key, value]) => (
            <div key={key} className="row t-sm" style={{ alignItems: "flex-start" }}>
              <LuLightbulb size={15} style={{ color: "var(--amber)", marginTop: 3, flex: "0 0 auto" }} />
              <span>
                {key}. {value}
              </span>
            </div>
          ))}
          {Object.keys(tips || {}).length === 0 ? <p className="t-sm">No tips available right now.</p> : null}
        </div>
      </Dialog>

      {/* ---------- text exam results overlay ---------- */}
      {resultOpen ? (
        <div className="aq-scrim" onClick={(e) => e.target === e.currentTarget && setResultOpen(false)}>
          <div className="aq-modal" role="dialog" aria-modal="true">
            <div className="row-between mb-2">
              <div className="t-h3">Exam results overview</div>
              <button
                type="button"
                onClick={() => setResultOpen(false)}
                aria-label="Close"
                style={{ color: "var(--ink-3)" }}
              >
                <LuX size={18} />
              </button>
            </div>
            <hr className="divider mb-3" />
            {resultError ? (
              <Note tone="danger">{resultError}</Note>
            ) : !resultData ? (
              <div className="stack-3">
                <Skel h={18} w="70%" />
                <Skel h={12} />
                <Skel h={12} w="85%" />
                <Skel h={18} w="60%" />
                <Skel h={12} />
              </div>
            ) : (
              <div className="stack-3">
                {(resultData.questions || []).map((q, index) => {
                  const r = (resultData.rating || [])[index];
                  return (
                    <div key={index} className="aq-qa">
                      <div className="aq-qahead">
                        <p className="aq-q">
                          Question {index + 1}: {resultData.questions[index][index + 1] || "N/A"}
                        </p>
                        <span className="aq-rate">{r !== undefined && r !== null && r !== "" ? `${r}/10` : "N/A"}</span>
                      </div>
                      <p className="t-sm mt-1">{(resultData.answers || [])[index] || "No Answer"}</p>
                      <div className="row mt-2" style={{ flexWrap: "wrap", gap: 8 }}>
                        <StarRating rating={(resultData.rating || [])[index]} />
                      </div>
                      <div className="mt-2">
                        <p className="aq-minihead">Feedback</p>
                        <p className="t-sm">{(resultData.feedback_comments || [])[index] || "No Feedback"}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {/* ---------- video interview feedback overlay ---------- */}
      {videoDocId ? (
        <div className="aq-scrim" onClick={(e) => e.target === e.currentTarget && setVideoDocId("")}>
          <div className="aq-modal" role="dialog" aria-modal="true">
            <div className="row-between mb-2">
              <div className="t-h3">Exam results overview</div>
              <button type="button" onClick={() => setVideoDocId("")} aria-label="Close" style={{ color: "var(--ink-3)" }}>
                <LuX size={18} />
              </button>
            </div>
            <hr className="divider mb-3" />
            <VideoFeedback docId={videoDocId} />
          </div>
        </div>
      ) : null}

      {/* ---------- busy overlay (resume in flight) ---------- */}
      {busy ? (
        <div className="aq-scrim">
          <div className="aq-busy">
            <Spinner lg />
            <div className="t-sm t-ink mt-2" style={{ fontWeight: 600 }}>
              {busy}
            </div>
            <div className="t-xs mt-1">This can take a moment — hang tight.</div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
